import React from "react";
import { useMobileDetection } from "../../hooks/use-mobile-detection";
import { Button } from "../ui/Button";
import "./mobile-connection-helper.scss";

interface MobileConnectionHelperProps {
  isVisible: boolean;
  onClose: () => void;
}

export function MobileConnectionHelper({
  isVisible,
  onClose,
}: MobileConnectionHelperProps) {
  const { isMobile, isIOS, isAndroid } = useMobileDetection();

  if (!isVisible || !isMobile) return null;

  const getCurrentUrl = () => {
    const protocol = window.location.protocol === "https:" ? "https:" : "http:";
    const host = window.location.host;
    return `${protocol}//${host}`;
  };

  return (
    <div className="mobile-connection-helper">
      <div className="helper-content">
        <div className="helper-header">
          <h3>📱 Mobile Setup Guide</h3>
          <Button variant="ghost" size="sm" onClick={onClose}>
            ✕
          </Button>
        </div>

        <div className="helper-body">
          <div className="step">
            <div className="step-number">1</div>
            <div className="step-content">
              <h4>Network Connection</h4>
              <p>
                Make sure your mobile device is on the same WiFi network as your
                computer.
              </p>
              <div className="url-info">
                <strong>Current URL:</strong>
                <code>{getCurrentUrl()}</code>
              </div>
            </div>
          </div>

          <div className="step">
            <div className="step-number">2</div>
            <div className="step-content">
              <h4>Browser Permissions</h4>
              <p>
                {isIOS &&
                  "On iOS Safari, you'll need to allow microphone access when prompted."}
                {isAndroid &&
                  "On Android Chrome, you'll need to allow microphone access when prompted."}
                {!isIOS &&
                  !isAndroid &&
                  "Allow microphone access when your browser prompts you."}
              </p>
            </div>
          </div>

          <div className="step">
            <div className="step-number">3</div>
            <div className="step-content">
              <h4>HTTPS Requirement</h4>
              <p>
                For microphone access, most mobile browsers require HTTPS. If
                you're having issues:
              </p>
              <ul>
                <li>Try using ngrok or a similar tunneling service</li>
                <li>Set up a local HTTPS certificate</li>
                <li>Use a cloud deployment</li>
              </ul>
            </div>
          </div>

          {isIOS && (
            <div className="step ios-specific">
              <div className="step-number">📱</div>
              <div className="step-content">
                <h4>iOS Safari Tips</h4>
                <ul>
                  <li>Add to Home Screen for better experience</li>
                  <li>Keep Safari in foreground during calls</li>
                  <li>Check Settings → Safari → Camera & Microphone</li>
                </ul>
              </div>
            </div>
          )}

          {isAndroid && (
            <div className="step android-specific">
              <div className="step-number">🤖</div>
              <div className="step-content">
                <h4>Android Chrome Tips</h4>
                <ul>
                  <li>Use Chrome for best compatibility</li>
                  <li>Check site permissions in browser settings</li>
                  <li>Ensure Chrome is up to date</li>
                </ul>
              </div>
            </div>
          )}
        </div>

        <div className="helper-footer">
          <Button variant="primary" onClick={onClose}>
            Got it!
          </Button>
        </div>
      </div>
    </div>
  );
}
